






























































































































import {
    defineComponent,
    ref,
    computed,
    useRouter,
    useContext,
    useFetch,
    watch,
} from "@nuxtjs/composition-api";
import { useLicensePlateFinder } from "~/modules/part-finder/composables/useLicensePlateFinder";
import { usePartFinderStore } from "../stores/partFinder";
import { storeToRefs } from "pinia";
import type { LicenseInformation } from "~/modules/part-finder/composables/useLicensePlateFinder";
import { SfButton, SfLoader, SfProperty } from "@storefront-ui/vue";
import WmSelect from "components/theme/Form/Select.vue";

export default defineComponent({
    name: "LicensePlateFinderSimple",
    props: {
        buttonText: {
            type: String,
            default: "",
        },
        placeholder: {
            type: String,
            default: "",
        },
    },
    components: {
        SfButton,
        SfLoader,
        SfProperty,
        WmSelect,
    },
    setup() {
        const licensePlate = ref("");
        const { loading, search, getMMYInfo } = useLicensePlateFinder();

        const result = ref<LicenseInformation | null>(null);
        const searchSent = ref(false);

        const finderId = ref<number | null>(null);
        const store = usePartFinderStore();
        const { finders } = storeToRefs(store);

        // Options derived from backend-provided finders list
        const typedFinders = computed(
            () => finders.value as Array<{ finder_id: number; name: string }>
        );

        const findersOptions = computed(() => {
            return (
                finders.value?.map((f) => ({
                    value: f.finder_id,
                    label: f.name,
                })) || []
            );
        });

        // UI control states
        const selectedBrandId = ref<string | null>(null);
        const selectedModelId = ref<string | null>(null);
        const selectedBrandName = ref<string | null>(null);
        const selectedBrandFinderId = ref<number | null>(null);

        // Used for dropdown population based on search results
        const selectedBrandEntry = ref<{
            brand: string;
            suggestions: typeof result.value.suggestions;
        } | null>(null);

        watch(selectedBrandFinderId, (newFinderId) => {
            if (!newFinderId || !selectedBrandEntry.value) return;

            // Find the corresponding suggestion
            const suggestion = selectedBrandEntry.value.suggestions.find(
                (s) => s.brand_finder_id === Number(newFinderId)
            );

            if (suggestion) {
                selectedBrandId.value = String(suggestion.brand_id);
                result.value.brand = suggestion.brand;
                result.value.brand_finder_id = suggestion.brand_finder_id;

                // Optionally auto-set model if only one model exists
                if (suggestion.models?.length === 1) {
                    selectedModelId.value = String(
                        suggestion.models[0].model_id
                    );
                    result.value.model = suggestion.models[0].model;
                }
            }
        });

        // Determine if model dropdown should be shown
        const shouldShowFinderSelect = computed(() => {
            const suggestions = selectedBrandEntry.value?.suggestions;
            return (
                Array.isArray(suggestions) &&
                suggestions.length >= 1 &&
                !loading.value
            );
        });

        const noResults = computed(() => {
            return (
                !result.value?.brand_exists &&
                !result.value?.model_exists &&
                result.value?.suggestions?.length == 0
            );
        });

        function updateSelectedBrandEntry() {
            selectedBrandEntry.value =
                uniqueBrandNames.value.find(
                    (b) => b.brand === selectedBrandName.value
                ) || null;
        }

        // Whether the backend confirmed brand/model (not needed to be replaced by suggestions)
        const hasSuggestions = computed(() => {
            return result.value?.suggestions?.length > 0;
        });

        const brandConfirmed = computed(
            () => result.value?.brand_exists ?? true
        );
        const modelConfirmed = computed(
            () => result.value?.model_exists ?? true
        );

        // Build a list of unique brand entries from suggestions
        const uniqueBrandNames = computed(() => {
            if (!result.value?.suggestions) return [];

            const brandMap = new Map<
                string,
                { brand: string; suggestions: typeof result.value.suggestions }
            >();

            for (const s of result.value.suggestions) {
                if (!s.brand) continue;

                if (!brandMap.has(s.brand)) {
                    brandMap.set(s.brand, { brand: s.brand, suggestions: [s] });
                } else {
                    brandMap.get(s.brand)!.suggestions.push(s);
                }
            }

            return Array.from(brandMap.values());
        });

        const finderOptionsForSelectedBrand = computed(() => {
            if (!selectedBrandName.value || !result.value?.suggestions)
                return [];

            return result.value.suggestions
                .filter((s) => s.brand === selectedBrandName.value)
                .map((s) => ({
                    finder_id: s.brand_finder_id,
                    finder_name:
                        finders.value?.find(
                            (f) => f.finder_id === s.brand_finder_id
                        )?.name || `Finder ${s.brand_finder_id}`,
                }));
        });

        const modelSuggestions = computed(() => {
            if (!result.value?.suggestions || !selectedBrandFinderId.value)
                return [];

            // Find the suggestion that matches both the selected brand ID and finder ID
            // Note: brand_id corresponds to brand ID, brand_finder_id corresponds to finder ID
            const brandEntry = result.value.suggestions.find(
                (s) =>
                    s.brand_finder_id === Number(selectedBrandFinderId.value) &&
                    s.brand_id === Number(selectedBrandId.value)
            );

            // Only models from the selected finder should be returned
            return brandEntry?.models || [];
        });

        const getFinderName = (brandFinderId: number) => {
            const finder = typedFinders.value.find(
                (f) => f.finder_id === brandFinderId
            );
            return finder?.name || '${$t("Finder")} #${brandFinderId}';
        };

        const brandSelected = computed(
            () => brandConfirmed.value || selectedBrandId.value !== null
        );

        // Determine if model dropdown should be shown
        const shouldShowModelSelect = computed(() => {
            return (
                hasSuggestions.value &&
                brandSelected.value &&
                !modelConfirmed.value &&
                modelSuggestions.value.length > 0 &&
                (!shouldShowFinderSelect.value || // finder dropdown has no options
                    (shouldShowFinderSelect.value &&
                        selectedBrandFinderId.value)) // finder dropdown shown & selected
            );
        });

        // Return whether we should allow user to proceed to search
        const canSubmit = computed(() => {
            const selectedBrand = result.value?.suggestions.find(
                (s) => Number(selectedBrandFinderId.value) === s.brand_finder_id
            );

            if (!selectedBrand) return false;

            // If brand has no models => allow submit right after brand is selected
            const hasModels =
                selectedBrand.models && selectedBrand.models.length > 0;

            if (!hasModels) {
                return !!selectedBrandName.value; // brand is selected, model doesn't matter
            }

            if (modelConfirmed.value) {
                return !!selectedBrandId.value;
            }

            // If brand has models => require both brand and model
            const selectedModel = selectedBrand.models.find(
                (m) => m.model_id === Number(selectedModelId.value)
            );

            return !!selectedBrandName.value && !!selectedModel;
        });

        function onBrandNameChange() {
            const entry =
                uniqueBrandNames.value.find(
                    (b) => b.brand === selectedBrandName.value
                ) || null;

            if (entry) {
                selectedBrandEntry.value = entry;

                // Auto-select finder if only one suggestion
                if (entry.suggestions.length === 1) {
                    const suggestion = entry.suggestions[0];
                    selectedBrandId.value = String(suggestion.brand_id);
                    selectedBrandFinderId.value = suggestion.brand_finder_id;
                    result.value.brand = suggestion.brand;
                    result.value.brand_finder_id = suggestion.brand_finder_id;

                    // Auto-select model if only one model exists
                    if (suggestion.models && suggestion.models.length === 1) {
                        selectedModelId.value = String(
                            suggestion.models[0].model_id
                        );
                        result.value.model = suggestion.models[0].model;
                    }
                } else {
                    selectedBrandId.value = null;
                    result.value.brand = selectedBrandName.value;
                    const fallbackFinder =
                        entry.suggestions[0]?.brand_finder_id;
                    if (fallbackFinder) {
                        result.value.brand_finder_id = fallbackFinder;
                    }
                }
            }
        }

        // Watch for model changes to update result object
        watch(selectedModelId, (newVal) => {
            if (!newVal || modelConfirmed.value) return;

            const selectedBrand = result.value?.suggestions.find(
                (s) => Number(selectedBrandId.value) === s.brand_id
            );

            const model = selectedBrand?.models?.find(
                (m) => m.model_id === Number(newVal)
            );
            if (model) {
                result.value!.model = model.model;
            }
        });

        async function searchForLicensePlate() {
            searchSent.value = false;
            const searchString = licensePlate.value.replace(/\s/g, "");
            result.value = await search(searchString);
            searchSent.value = true;

            const hasExactMatch =
                result.value?.brand_exists &&
                result.value?.model_exists &&
                result.value?.brand_finder_id;

            if (
                !brandConfirmed.value &&
                result.value?.suggestions?.length === 1
            ) {
                const suggestion = result.value.suggestions[0];

                selectedBrandName.value = suggestion.brand;
                updateSelectedBrandEntry();
                selectedBrandFinderId.value = suggestion.brand_finder_id;
                selectedBrandId.value = String(suggestion.brand_id);
                result.value.brand = suggestion.brand;
                result.value.brand_finder_id = suggestion.brand_finder_id;
            }

            if (uniqueBrandNames.value.length === 1) {
                const onlyBrand = uniqueBrandNames.value[0];
                selectedBrandName.value = onlyBrand.brand;
                updateSelectedBrandEntry();

                if (onlyBrand.suggestions.length === 1) {
                    const suggestion = onlyBrand.suggestions[0];
                    selectedBrandId.value = String(suggestion.brand_id); // ✅ Set brand ID
                    selectedBrandFinderId.value = suggestion.brand_finder_id;
                    result.value.brand = suggestion.brand;
                    result.value.brand_finder_id = suggestion.brand_finder_id;

                    // Auto-select model if only one
                    if (suggestion.models?.length === 1) {
                        selectedModelId.value = String(
                            suggestion.models[0].model_id
                        );
                        result.value.model = suggestion.models[0].model;
                    }
                }
            }

            if (!selectedBrandEntry.value && selectedBrandName.value) {
                updateSelectedBrandEntry();
            }

            if (!modelConfirmed.value && modelSuggestions.value.length === 1) {
                const suggestion = modelSuggestions.value[0];
                selectedModelId.value = String(suggestion.model_id);
                result.value.model = suggestion.model;
            }

            const shouldRedirect =
                (brandConfirmed.value || selectedBrandId.value) &&
                (modelConfirmed.value || selectedModelId.value) &&
                result.value?.brand &&
                result.value?.model &&
                result.value?.brand_finder_id &&
                result.value?.suggestions?.length <= 1;

            if (shouldRedirect && result.value) {
                const info = await getMMYInfo({
                    brand: result.value.brand,
                    model: result.value.model,
                    finder_id: result.value.brand_finder_id,
                });

                if (info.brand && info.finder && info.model) {
                    const route = localeRoute({
                        name: "part-finder-parts",
                        params: {
                            brand: info.brand.slug,
                            finder: info.finder.slug,
                            model: info.model.slug,
                        },
                    });
                    router.push(route);
                } else if (info.brand) {
                    const route = localeRoute({
                        name: "part-finder-models",
                        params: {
                            finder: info.finder.slug,
                            brand: info.brand.slug,
                        },
                    });
                    router.push(route);
                } else if (info.finder) {
                    const route = localeRoute({
                        name: "part-finder-finders",
                        params: {
                            finder: info.finder.slug,
                        },
                    });
                    router.push(route);
                } else {
                    router.push(localeRoute({ name: "part-finder" }));
                }
            }
        }

        useFetch(async () => {
            if (!finders.value?.length) {
                await store.loadFinders();
            }
        });

        const router = useRouter();
        const {
            localeRoute,
            app: { i18n },
        } = useContext();
        const findMMYError = ref("");
        async function findMMYInfo() {
            let brandFinderId = Number(selectedBrandFinderId.value);
            if (selectedBrandEntry.value && selectedBrandId.value) {
                const selected = selectedBrandEntry.value.suggestions.find(
                    (s) => s.brand_id === Number(selectedBrandId.value)
                );
                if (selected) {
                    result.value.brand = selected.brand;
                    result.value.brand_finder_id = selected.brand_finder_id;
                }
            }
            if (selectedModelId.value && !modelConfirmed.value) {
                const brandEntry = result.value?.suggestions.find(
                    (s) =>
                        brandConfirmed.value ||
                        Number(selectedBrandId.value) === s.brand_id
                );

                const selected = brandEntry?.models?.find(
                    (m) => m.model_id === Number(selectedModelId.value)
                );

                if (selected) {
                    result.value.model = selected.model;
                }
            }

            const info = await getMMYInfo({
                brand: result.value.brand,
                model: result.value.model,
                finder_id: brandFinderId,
            });
            if (info.brand && info.finder && info.model) {
                const route = localeRoute({
                    name: "part-finder-parts",
                    params: {
                        brand: info.brand.slug,
                        finder: info.finder.slug,
                        model: info.model.slug,
                    },
                });
                router.push(route);
            } else if (info.brand) {
                const route = localeRoute({
                    name: "part-finder-models",
                    params: {
                        finder: info.finder.slug,
                        brand: info.brand.slug,
                    },
                });
                router.push(route);
            } else if (info.finder) {
                const route = localeRoute({
                    name: "part-finder-finders",
                    params: {
                        finder: info.finder.slug,
                    },
                });
                router.push(route);
            } else {
                router.push(localeRoute({ name: "part-finder" }));
            }
        }

        function resetSearch() {
            licensePlate.value = "";
            result.value = null;
            searchSent.value = false;
            selectedBrandId.value = null;
            selectedModelId.value = null;
            selectedBrandName.value = null;
            selectedBrandFinderId.value = null;
            selectedBrandEntry.value = null;
            findMMYError.value = "";
        }

        return {
            licensePlate,
            loading,
            searchForLicensePlate,
            result,
            searchSent,
            findMMYInfo,
            findMMYError,
            finderId,
            findersOptions,
            hasSuggestions,
            brandConfirmed,
            modelConfirmed,
            selectedBrandId,
            selectedModelId,
            uniqueBrandNames,
            modelSuggestions,
            brandSelected,
            resetSearch,
            selectedBrandName,
            selectedBrandFinderId,
            finderOptionsForSelectedBrand,
            selectedBrandEntry,
            onBrandNameChange,
            getFinderName,
            canSubmit,
            shouldShowFinderSelect,
            shouldShowModelSelect,
            noResults,
        };
    },
});
